import React, { useState, useEffect, useRef, useCallback } from 'react'
import Joyride, { CallBackProps, STATUS } from 'react-joyride';
import useLocalStorage from '../../hooks/local-storage';
import { useSelector, useDispatch } from 'react-redux'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { TouchBackend } from 'react-dnd-touch-backend'
import Progression from './Progression'
import LoopAttributes from './LoopAttributes'
import LoopMap from './LoopMap'
import ClipAttributes from './ClipAttributes'
import EditorLibrary from './EditorLibrary'
import { resetEditor, takeSnapshot, clearClips, setStatus, setCycle, setSnap, setDuration } from './editorSlice'
import { getProgression, getNumeralInMajor, useKeyPressing, bufferToWav, calculateDurationInSeconds } from './editorUtils'
import { CustomDragLayer } from './CustomDragLayer'
import axios from 'axios'
import applyCaseMiddleware from 'axios-case-converter'
import { CircularProgress, IconButton, OutlinedInput, Tooltip, DialogTitle, DialogContent, DialogContentText, Dialog, DialogActions, Button, Backdrop, Collapse, ListItemIcon} from '@mui/material'
import {StyledMenu, StyledMenuItem} from '../../components/StyledSelect'
import { makeStyles } from '@mui/styles'
import Confirmation, {StyledDialog} from '../../components/Confirmation'
import StyledButton from '../../components/StyledButton'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import EditIcon from '@mui/icons-material/Edit'
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import ErrorOutlineOutlinedIcon from '@mui/icons-material/ErrorOutlineOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RepeatIcon from '@mui/icons-material/Repeat';
import userStore from '../common/userStore';
import {ReactComponent as SaveIcon} from './icons/save.svg'
import {ReactComponent as ClearIcon} from './icons/clear.svg'
import {ReactComponent as SnapOnIcon} from './icons/snap-on.svg'
import {ReactComponent as SnapOffIcon} from './icons/snap-off.svg'
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import download from '../account/download'
import useWindowSize from '../../hooks/window-size';
import './Editor.css'
import { shareDialogStore } from '../share/ShareDialog'
import ReplyIcon from '@mui/icons-material/Reply';

const Wrap = (props) => {
  //onClose return true or false to tell caller if need to refresh list
  //if open from existing loop, url is the preview audio url
  const { setHandleClose, onClose, id, name, packId, parentId, url, editOwnLoop, alreadyAdd, preview} = props;
  console.log(id)
  const classes = useStyles();
  const dispatch = useDispatch();
  const status = useSelector(state => state.editor.status);
  const json = useSelector(state => state.editor.json);
  const snapshot = useSelector(state => state.editor.snapshot);
  const config = useSelector(state => state.editor.config);
  const fx = useSelector(state => state.editor.fx);
  const bars = useSelector(state => state.editor.bars);
  const key = useSelector(state => state.editor.key);
  const mode = useSelector(state => state.editor.mode);
  const clips = useSelector(state => state.editor.clips);
  const bpm = useSelector(state => state.editor.bpm);
  const cycle = useSelector(state => state.editor.cycle);
  const snap = useSelector(state => state.editor.snap);
  const [hasRendered, setHasRendered] = useState(false);
  const [wavesurfer, setWavesurfer] = useState(null);
  const [sessionId, setSessionId] = useState(editOwnLoop ? id : -1);
  const [sessionName, setSessionName] = useState(name);
  const [nameHovering, setNameHovering] = useState(false);
  const [editNameText, setEditNameText] = useState(name);
  const [editNameOpen, setEditNameOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [tempUrl, setTempUrl] = useState('');
  const [warningOpen, setWarningOpen] = useState(false);
  const [warningText, setWarningText] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const [showProgression, setShowProgression] = useState(false);
  const [progressionDropItem, setProgressionDropItem] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const spaceKeyPressing = useKeyPressing('Space', 32);
  const minHeight = 393;
  const windowSize = useWindowSize();

  const progression = getProgression(clips, bars);
  const renderData = { fx: fx, bars: bars, key:key, mode:mode, bpm:bpm, clips: clips };
  const needRender = JSON.stringify(snapshot) !== JSON.stringify(renderData);
  const data = {...renderData, id: sessionId, packId: packId, name: sessionName, progression: progression.map(change => getNumeralInMajor(key, mode, change.root) + ' ' + change.type),
                instrument: config.instruments && clips.length > 0 ? config.groups.find(group => group.id === clips[0].id && group.type === clips[0].type).instrument : null};
  const renderJson = json !== null ? { fx: json.fx, bars: json.bars, key: json.key, mode: json.mode, bpm: json.bpm, clips: json.clips} : null;
  const sessionChanged = JSON.stringify(renderJson) !== JSON.stringify(renderData);
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '') || /iPad|iPhone|iPod/i.test(navigator.userAgent || '');

  useEffect(() => {
    if (spaceKeyPressing && (status === 'editing' || status === 'playing') && !showProgression)
    {
      document.querySelector("div[aria-labelledby='editor']").focus();
      handlePlayStopButtonClick();
      document.querySelector("div[aria-labelledby='editor']").blur();
    }
  }, [spaceKeyPressing]);

  let height = windowSize.height * 0.75;
  if (height < minHeight)
    height = minHeight;

  function downloadAndClose(ret, midi = 0)
  {
    dispatch(setStatus('saving'));
    download(data.id === -1 ? id : data.id, data, midi).then(resp => {
      onClose(ret);
    })
  }

  function shareAndClose(ret)
  {
    onClose(ret);
    shareDialogStore.showModal(data.id === -1 ? id : data.id);
  }

  function save(download = false, midi = 0, saveAs = false, share = false)
  {
    const close = share ? shareAndClose : (download ? downloadAndClose : onClose);
    if (sessionChanged || !hasRendered || saveAs)
    {
      const client = applyCaseMiddleware(axios.create());
      if (wavesurfer.isPlaying())
        wavesurfer.stop();
      dispatch(setStatus('saving'));
      const saveSession = (saveUrl) => {
        wavesurfer.unAll();
        wavesurfer.load(saveUrl);
        wavesurfer.on(isSafari ? "waveform-ready" : "ready", () => {
            wavesurfer.exportPCM(512, 10000, true).then(thumbnail => {
                data.thumbnail = thumbnail;
                client.post('/session/save', data).then(() => {wavesurfer.destroy(); close(true, midi)});
            });
      })}
      if (sessionId === -1 || needRender || !hasRendered || saveAs)
      {
          client.post('/session/' + (sessionId === -1 || saveAs ? 'add' : 'modify'), data, {timeout : 3500}).then(resp => {
              if (sessionId === -1 || saveAs)
                data.id = resp.data.data.id;
              saveSession(resp.data.data.tempUrl);
          }).catch(err => {
            showWarning('Network error. Please try again later')
            dispatch(setStatus('editing'));
          });   
      }
      else
        saveSession(tempUrl);
    }
    else
    {
      wavesurfer.destroy();
      if (json !== null && sessionName !== name)
      {
        dispatch(setStatus('saving'));
        axios.post('/loop/update', {}, { params:{loopId: id, name:sessionName}} ).then(resp => close(true));
      }
      else
        close(false, midi);
    } 
  }

  function discard()
  {
    setShowProgression(false);
    if (wavesurfer !== null)
      wavesurfer.destroy();
    if (sessionId !== -1 && !editOwnLoop && userStore.isLogin) //only discard if not modifying own loop
      axios.post('/session/discard', {id: sessionId}).then(() => onClose(false));
    else
      onClose(false);
  }

  function handleClose()
  {
    if (status !== 'saving' && status !== 'requesting')
    {
      if (clips.length > 0 && userStore.isLogin && (sessionChanged || (json !== null && sessionName !== name)))
        setCloseOpen(true);
      else
        discard();
    }
  }

  function showWarning(text)
  {
    setWarningText(text);
    setWarningOpen(true);
    setTimeout(() => {setWarningOpen(false)}, 1500);
  }

  function handlePlayStopButtonClick()
  {
      if (clips.length === 0)
        showWarning("Loop is empty");
      else if (status === 'playing')
      {
          wavesurfer.stop();
          dispatch(setStatus('editing'));
      }
      else if(wavesurfer !== null)
      {
          if (wavesurfer.isPlaying())
              wavesurfer.stop();
          dispatch(setStatus('requesting'));
          const play = (playUrl) => {
              wavesurfer.unAll();
              wavesurfer.load(playUrl);
              // const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent || '') || /iPad|iPhone|iPod/i.test(navigator.userAgent || '');
              // if ()//!isSafari || !hasRendered//isSafari && !hasRendered
              // {
              //   wavesurfer.load(playUrl);
              //   setHasRendered(true);
              // }
              // else
              // {
              //   const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
              //   const request = new XMLHttpRequest();
              //   request.open('GET', playUrl, true);
              //   request.responseType = 'arraybuffer';
              //   request.onload = function() {
              //     const audioData = request.response;
              //     audioCtx.decodeAudioData(audioData, function(buffer) {
              //       const numSamples = Math.floor(bars * NUM_BEATS_PER_BAR * 60 / bpm * buffer.sampleRate);
              //       const numAttackSamples = Math.floor(5 * buffer.sampleRate / 1000);
              //       const diff = buffer.length - numSamples;
              //       console.log(buffer.length);
              //       console.log(numSamples);
              //       const loopBuffer = audioCtx.createBuffer(buffer.numberOfChannels, numSamples, buffer.sampleRate);
              //       for (let i = 0; i < buffer.numberOfChannels; i++)
              //       {
              //         for (let j = 0; j < numSamples; j++)
              //           loopBuffer.getChannelData(i)[j] = buffer.getChannelData(i)[j + diff];
              //         for (let j = 0; j < numAttackSamples; j++)
              //           loopBuffer.getChannelData(i)[j] *= j / numAttackSamples;
              //       }
              //       if (isSafari)
              //         wavesurfer.load(URL.createObjectURL(bufferToWav(loopBuffer, numSamples)));
              //       else
              //        wavesurfer.loadDecodedBuffer(loopBuffer);
              //       audioCtx.close();
              //     },
              //     function(e){ console.log("Error with decoding audio data" + e.err); });
              //   }
              //   request.send();
              // }
              wavesurfer.on("ready", () => {
                  let duration = wavesurfer.getDuration();
                  let diff = duration - calculateDurationInSeconds(bpm, bars);
                  if (diff < 0)
                  {
                    diff = 0;
                    duration = calculateDurationInSeconds(bpm, bars);
                  }
                  else if (isSafari)
                    diff += 0.1;
                  else
                    diff = 0;
                  dispatch(setDuration(duration));
                  dispatch(setStatus('playing'));
                  // wavesurfer.clearRegions();
                  // const region = wavesurfer.addRegion({
                  //   start: diff / 2,
                  //   end: duration - diff / 2,
                  //   loop: cycle,
                  //   drag: false,
                  //   resize: false,
                  //   color: 'rgba(0, 0, 0, 0)'
                  // });
                  // const promise = cycle ? region.playLoop() : wavesurfer.play();
                  const promise = wavesurfer.play();
                  if (promise !== undefined) {
                    promise.catch(error => {
                      dispatch(setStatus('editing'));
                    })
                  }
              })
              wavesurfer.on("finish", () => {
                  dispatch(setStatus('editing'));
              })
          };
          if (needRender)
          {
              const client = applyCaseMiddleware(axios.create());
              client.post('/session/' + (sessionId === -1 ? 'add' : 'modify'), data, {timeout : 3500}).then(resp => {
                  if (sessionId === -1)
                      setSessionId(resp.data.data.id);
                  play(resp.data.data.tempUrl);
                  setTempUrl(resp.data.data.tempUrl);
                  dispatch(takeSnapshot(renderData));
              }).catch(err => {
                showWarning('Network error. Please try again later')
                dispatch(setStatus('editing'));
              }); 
              setHasRendered(true);
          }
          else
              play(tempUrl === '' ? url : tempUrl);
      }
  }

  function handleSaveButtonClick(download = false, midi = 0, saveAs = false, share = false)
  {
      if (clips.length === 0)
        showWarning("Cannot save empty loop");
      else
      {
        if (sessionName === name && parentId)
          data.name = name + " Edited";
        save(download, midi, saveAs, share);
      }
  }

  function handleAddButtonClick(download = false, midi = 0, share = false)
  {
      if (clips.length === 0)
        showWarning("Cannot add empty loop");
      else if(userStore.isAnonymous)
      { 
        userStore.setOpenSignup(true);
      }
      else if(sessionChanged)
      {
        if (sessionName === name && json !== null)
          data.name = name + " Edited";
        save(download, midi, false, share);
      }
      else if(alreadyAdd)
      {
        if (download)
          downloadAndClose(false, midi);
        else if(share)
          shareAndClose(false);
        else
          showWarning("You've already added this loop");
      }
      else
      {
        dispatch(setStatus('saving'));
        axios.post('/loop/addToMyLoop', {}, {params: {loopId: id, name:sessionName}}).then(resp => share ? shareAndClose(true) : (download ? downloadAndClose(true, midi) : onClose(true)));
      }
  }

  setHandleClose(handleClose)

  useEffect(() => {
    return () => {
      setHandleClose(null)
      dispatch(resetEditor());
    }
  }, [dispatch])

  const [onboard, setOnboard] = useLocalStorage('onboard', '0')
  const [run, setRun] = useState(onboard !=  '1');
  
  const handleJoyrideCallback = (data) => {
    const { status, type } = data;
    
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED, STATUS.PAUSED];

    if (finishedStatuses.includes(status)) {
      setOnboard('1');
      setRun(false);
    }

    
  };

  return <>
      <Joyride
        continuous
        callback={handleJoyrideCallback}
        hideBackButton
        hideCloseButton
        run={run}
        showProgress
        showSkipButton
        disableOverlayClose
        disableScrolling
        steps={[
          {
            content: <h2>Let's begin our journey!</h2>,
            placement: 'center',
            target: 'body',
          },
          {
            content: 'Drag & drop grooves and licks to the edit area to make a loop',
            target: '.editor-library',
          },
          {
            content: "Change progression or drop a groove to find inspirations",
            target: '.progression-select',
          },
          {
            content: "Click or drag clips individually to fine adjust the loop",
            target: '.loop-map',
          },
          {
            content: "For more tips and shortcuts, check FAQ section",
            placement: 'center',
            target: 'body'
          }
        ]}
        styles={{
          options: {
            zIndex: 10000,
            primaryColor: '#FF6500',
          },
        }}
      />
      <Backdrop open={status === 'saving'} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress />
      </Backdrop>
      <Tooltip open={warningOpen} placement="top" classes={{tooltip: classes.tooltip}}
            title={<div style={{display: 'flex', alignItems: 'center'}}><ErrorOutlineOutlinedIcon style={{marginRight: '5px'}}/>{warningText}</div>}>
      <Collapse in={showProgression} collapsedSize={minHeight} >
        <Progression height={height} progressionDropItem={progressionDropItem} showProgression={showProgression} setShowProgression={setShowProgression} wavesurfer={wavesurfer} packId={packId} showWarning={showWarning}/>
        <DndProvider backend={HTML5Backend}>
          <CustomDragLayer />
          <StyledDialog
          open={closeOpen}
          aria-labelledby="close-dialog-title"
          aria-describedby="close-dialog-description"
          >
          <DialogTitle id="close-dialog-title">{sessionName}</DialogTitle>
          <DialogContent>
              <DialogContentText id="close-dialog-description">{editOwnLoop ? 'Save the loop before closing?' : 'Add the loop before closing?'}</DialogContentText>
          </DialogContent>
          <DialogActions>
              <StyledButton onClick={() => {setCloseOpen(false); discard(); }} color="secondary">{editOwnLoop ? "Don't save" : "Don't add"}</StyledButton>
              <StyledButton onClick={() => setCloseOpen(false)} color="secondary">Cancel</StyledButton>
              <Button onClick={() => {setCloseOpen(false); save();}} color="primary">OK</Button>
          </DialogActions>
          </StyledDialog>
          <div className='editor' style={{height: height, display: showProgression ? 'none' : 'flex'}} aria-labelledby='editor' tabIndex={0}>
            <div className='editor-project'>
              <div className={status === 'saving' ? 'editor-header disabled': 'editor-header'}>
                <div className="editor-header-left">
                    {status === 'requesting' ? <CircularProgress style={{marginLeft: 11, width: 24, height: 24, marginRight: 14}} /> : 
                      <IconButton color="primary" style={{fontSize: '30px', marginRight: 3}} onClick={() => handlePlayStopButtonClick()}>
                          {status === 'playing' ? <StopCircleOutlinedIcon fontSize="inherit"/> : <PlayCircleOutlineIcon fontSize="inherit"/>}
                      </IconButton>
                    }
                    {editNameOpen ? <OutlinedInput autoFocus style={{width: editOwnLoop ? 340 : 460, height: '26px'}} color="primary" value={editNameText} onChange={e => setEditNameText(e.target.value)} 
                    onBlur={() => {if (editNameText.trim() !== '') setSessionName(editNameText.trim()); setEditNameOpen(false); setNameHovering(false);}} 
                    onFocus={e => e.target.select()}
                    onKeyDown={(ev) => {
                        ev.stopPropagation();
                        if (ev.key === 'Enter' || ev.key === 'Escape') 
                        {
                            if (ev.key === 'Escape')
                                setEditNameText(sessionName);
                            setTimeout(() => {
                                ev.target.blur();
                            }, 50);
                        }
                    }}/> 
                    : <StyledButton disabled={status !== 'editing'} size="large" onMouseEnter={() => setNameHovering(true)} onMouseLeave={() => setNameHovering(false)} onClick={() => {setEditNameText(sessionName);setEditNameOpen(!editNameOpen);}}
                    style={{padding: 6, maxWidth: editOwnLoop ? 340 : 460, display: nameHovering ? 'flex' : 'inline-block', overflow: 'hidden', textOverflow: 'ellipsis'}} endIcon={nameHovering ? <EditIcon style={{fontSize: '16px', transform: 'translate(-5px, 0px)'}} /> : null}>{sessionName}</StyledButton>}
                </div>
                <div className={status !== 'editing' ? 'editor-header-right disabled' : "editor-header-right"}>
                    {/* <StyledButton variant="text" onClick={() => dispatch(setCycle(!cycle))}  style={{color: cycle ? '#FF6500' : '#5A5A5A', marginRight: '6px'}} 
                    startIcon={<RepeatIcon />}>Cycle</StyledButton>  */}
                    <StyledButton variant="text" onClick={() => dispatch(setSnap(!snap))}  style={{color: snap ? '#FF6500' : '#5A5A5A', marginRight: '6px'}} 
                    startIcon={ snap ? <SnapOnIcon style={{width: 20, height: 20}}/> : <SnapOffIcon style={{width: 20, height: 20}}/> }>Snap</StyledButton> 
                    <StyledButton variant="text" onClick={() => setClearOpen(true)} style={{marginRight: '6px'}} startIcon={<ClearIcon style={{width: 20, height: 20}}/> }>Clear</StyledButton>
                    {editOwnLoop ? 
                    <>
                      <StyledButton variant="text" onClick={() => handleSaveButtonClick()} style={{marginRight: '6px'}} startIcon={ <SaveIcon style={{width: 20, height: 20}}/> }>Save</StyledButton> 
                      <StyledButton variant="text" onClick={() => handleSaveButtonClick(false, 0, true)} style={{marginRight: '6px'}} startIcon={ <ContentCopyIcon /> }>Save as new</StyledButton> 
                    </> :
                    <>
                      <StyledButton variant="text" onClick={() => handleAddButtonClick()} style={{marginRight: '6px'}} startIcon={ <AddCircleOutlineIcon /> }>Add</StyledButton> 
                    </>}
                    <StyledButton variant="text" startIcon={<SaveAltIcon />} style={{marginRight: '3px'}} onClick={(e) => {e.stopPropagation(); config.midi ?  setDownloadAnchorEl(Boolean(downloadAnchorEl) ? null : e.currentTarget) : (editOwnLoop ? handleSaveButtonClick(true) : handleAddButtonClick(true))}}>
                      Download
                    </StyledButton>
                    <StyledButton variant="text" startIcon={<ReplyIcon style={{transform: 'scaleX(-1)'}}/>} style={{marginRight: '15px'}} onClick={() => { editOwnLoop ? handleSaveButtonClick(false, 0, false, true) : handleAddButtonClick(false, 0, true)}}>
                      Share
                    </StyledButton>
                    <StyledMenu 
                      style={{transform: 'translateY(-13px)'}}
                      id="download-menu"
                      anchorEl={downloadAnchorEl}
                      open={Boolean(downloadAnchorEl)}
                      onClose={() => setDownloadAnchorEl(null)}
                      transitionDuration={{ appear:150, enter: 150, exit: 0 }}
                    >
                        <StyledMenuItem style={{paddingLeft: 8}} onClick={() => { setDownloadAnchorEl(null); editOwnLoop ? handleSaveButtonClick(true) : handleAddButtonClick(true)}}>
                          <ListItemIcon><GraphicEqIcon fontSize="small" /></ListItemIcon>Audio
                        </StyledMenuItem>
                        <StyledMenuItem style={{paddingLeft: 2}} onClick={() => { setDownloadAnchorEl(null); editOwnLoop ? handleSaveButtonClick(true, 1) : handleAddButtonClick(true, 1)}}>
                          <ListItemIcon><MusicNoteIcon fontSize="small" /></ListItemIcon>MIDI
                        </StyledMenuItem>
                    </StyledMenu>
                </div>
                <Confirmation open={clearOpen} setOpen={setClearOpen} onSubmit={() => dispatch(clearClips())} content={'Remove all clips?'}/>
              </div>
              <LoopAttributes showProgression={showProgression} setShowProgression={setShowProgression} setProgressionDropItem={setProgressionDropItem} showWarning={showWarning}/>
              <LoopMap setWavesurfer={setWavesurfer} offsetX={8} stepWidth={15} isViewOnly={false}/>
              <ClipAttributes showProgression={showProgression} wavesurfer={wavesurfer} packId={packId} preview={preview}/>
            </div>
            <EditorLibrary wavesurfer={wavesurfer} packId={packId} showProgression={showProgression} preview={preview}/>
          </div> 
        </DndProvider>
      </Collapse>
      </Tooltip>
  </>
}

const useStyles = makeStyles(theme => ({
  tooltip: {
    backgroundColor: theme.palette.primary.main,
    fontSize: '14px',
    minHeight: '22px',
    lineHeight: '22px'
  }
}));

export default function Editor(props) {
  const { open } = props;
  const config = useSelector(state => state.editor.config);
  const closeRef = useRef();
  const setHandleClose = useCallback((fun) => closeRef.current = fun, [closeRef])
  useEffect(() => {
      window.onbeforeunload = confirmExit;
      function confirmExit()
      {
        if (open)
          return "show warning";
      }
  }, [open])

  return (
    <Dialog open={config !== null && open} onClose={() => closeRef.current && closeRef.current()} aria-labelledby='editor-dialog' maxWidth='xl'>
      <Wrap {...props} setHandleClose={setHandleClose} />
    </Dialog>
  );
}
